import { useEffect, useState, type RefObject } from "react";

export const DASHLY_OVERLAY_HOST_SELECTOR = "[data-dashly-overlay-host], .p-dialog-mask, .p-dialog";

/**
 * PrimeReact overlay panels inside dialogs must append to `document.body`.
 *
 * Appending to `.p-dialog-mask` (position: fixed) breaks PrimeReact's `absolutePosition`,
 * which adds `window.scrollY` — dropdown panels end up off-screen when the page is scrolled.
 * Body-mounted panels are kept above dialogs via z-index rules in AppDialog / dashlyFormControl SCSS.
 */
export const resolveDashlyOverlayAppendTo = (
    element: HTMLElement | null | undefined
): HTMLElement | undefined => {
    if (typeof document === "undefined") {
        return undefined;
    }

    let node: HTMLElement | null = element ?? null;
    while (node && node !== document.body) {
        if (node.classList.contains("p-dialog") || node.matches("[data-dashly-overlay-host]")) {
            return document.body;
        }

        node = node.parentElement;
    }

    return document.body;
};

export const useDashlyOverlayAppendTo = (
    elementRef: RefObject<HTMLElement | null>
): HTMLElement | undefined => {
    const [appendTo, setAppendTo] = useState<HTMLElement | undefined>(() =>
        resolveDashlyOverlayAppendTo(elementRef.current)
    );

    useEffect(() => {
        const updateAppendTo = () => {
            setAppendTo(resolveDashlyOverlayAppendTo(elementRef.current));
        };

        updateAppendTo();
        document.addEventListener("focusin", updateAppendTo, true);

        return () => {
            document.removeEventListener("focusin", updateAppendTo, true);
        };
    }, [elementRef]);

    return appendTo;
};
